<template>
  <AccountLayout active-menu="account-settings">
    <div class="settings-page">
      <h1>Account Settings</h1>
      <h2>Please fill all fields in English only</h2>
      <p>Click on field value to edit</p>
      <table class="profile-table">
        <tbody>
          <tr v-for="(field, key) in fields" :key="key">
            <th>{{ field.label }}</th>
            <td>
              <!-- 只读字段 -->
              <template v-if="field.readonly">
                <span class="field-value has-value">{{ field.value }}</span>
              </template>
              <!-- 可编辑字段：展示模式 -->
              <template v-else-if="editing !== key">
                <span class="field-value" :class="{ 'has-value': field.value, 'empty-value': !field.value }"
                  @dblclick="startEdit(key)">
                  {{ field.value || 'Click to edit' }}
                </span>
              </template>
              <!-- 可编辑字段：编辑模式 -->
              <template v-else>
                <el-input ref="editInput" v-model="tempValue" :placeholder="field.placeholder" class="edit-input"
                  @keydown.enter.native="confirmEdit(key)" @keydown.esc.native="cancelEdit" />
                <button @click="confirmEdit(key)">OK</button>
                <button class="cancel-btn" @click="cancelEdit">CANCEL</button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <section class="plain-section">
        <h2>Status</h2>
        <p>International Retail</p>
      </section>

      <section class="settings-block">
        <h2>Change E-Mail</h2>
        <div class="settings-box">
          <div class="panel-row"><label>Current E-Mail</label><span>{{ currentEmail }}</span></div>
          <div class="panel-row"><label>New E-Mail</label><el-input v-model="newEmail" class="panel-input" /></div>
          <button class="primary-btn" :disabled="loading" @click="changeEmail">Change E-mail</button>
        </div>
      </section>

      <section class="settings-block">
        <h2>Notification settings</h2>
        <div class="settings-box">
          <strong>Receive E-Mail notifications about:</strong>
          <label class="check-line"><input type="checkbox" v-model="notifyInvoices" class="custom-checkbox"><span
              class="check-box"></span><span>new invoices</span></label>
          <label class="check-line"><input type="checkbox" v-model="notifyOrderStatus" class="custom-checkbox"><span
              class="check-box"></span><span>order status changes, payments and all other</span></label>
          <button class="primary-btn" :disabled="loading" @click="saveNotifications">Save me</button>
        </div>
      </section>

      <section class="settings-block">
        <h2>Change password</h2>
        <div class="settings-box">
          <div class="panel-row"><label>Current password</label><el-input v-model="password_form.current_password"
              type="password" class="panel-input" /></div>
          <div class="panel-row"><label>New password<br>(6 or more characters)</label><el-input
              v-model="password_form.password" type="password" class="panel-input" /></div>
          <div class="panel-row"><label>Re-enter new password</label><el-input
              v-model="password_form.password_confirmation" type="password" class="panel-input" />
          </div>
          <button class="primary-btn" :disabled="loading" @click="changePassword">Change password</button>
        </div>
      </section>

      <section class="settings-block">
        <h2>Privacy</h2>
        <div class="settings-box privacy-box">
          <label class="privacy-line"><input type="checkbox" v-model="allowAddressUse" class="custom-checkbox"><span
              class="check-box"></span><span>I allow buybuygenuine Trading co., Ltd. to store and use my address
              information.
              This information will only be used to process my orders and may be forwarded to shipping carriers for
              delivery.</span></label>
          <label class="privacy-line"><input type="checkbox" v-model="allowAnalyticsCookies"
              class="custom-checkbox"><span class="check-box"></span><span>I allow optional analytics cookies on this
              site. Permission granted on Jun 25,
              2026 03:10:31 pm.</span></label>
          <p>Visit the <a>
              <router-link to="/terms?id=5" @click.native="close">privacy policy page</router-link>
            </a>.</p>
          <button class="primary-btn delete-btn" @click="deleteAccount">Delete my account</button>
        </div>
      </section>
    </div>
  </AccountLayout>
</template>

<script>
import AccountLayout from '@/components/common/AccountLayout.vue'
import { removeToken, getToken } from '@/util/token-util.js'

export default {
  name: 'AccountSettingsPage',
  components: {
    AccountLayout
  },
  data() {
    return {
      fields: {
        accountType: { label: 'Account type', value: 'Person', readonly: true },
        givenName: { label: 'Given Name(s)', value: '', placeholder: 'Click to edit' },
        familyName: { label: 'Family Name', value: '', placeholder: 'Click to edit' },
        currency: { label: 'Preferred currency', value: 'USD', readonly: true },
        timezone: { label: 'Timezone', value: 'UTC +9.00 Seoul, Osaka, Tokyo', readonly: true }
      },
      editing: null,
      tempValue: '',
      // Change E-Mail
      currentEmail: '',
      newEmail: '',
      // Change password
      password_form: {
        current_password: '',
        password: '',
        password_confirmation: ''
      },
      // Notification
      notifyInvoices: true,
      notifyOrderStatus: false,
      // Privacy
      allowAddressUse: false,
      allowAnalyticsCookies: false,
      loading: false
    }
  },
  mounted() {
    if (!getToken()) {
      this.$message.error('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    this.loadUserInfo()
    this.loadNotificationSettings()
  },
  methods: {
    loadUserInfo() {
      this.$api({
        url: 'User/info',
        method: 'get'
      }).then(res => {
        const user = res.data || {}
        this.currentEmail = user.email || ''
        this.fields.givenName.value = user.given_name || ''
        this.fields.familyName.value = user.family_name || ''
        this.fields.currency.value = user.currency || ''
        this.fields.timezone.value = user.timezone || ''
      }).catch(() => {
        this.$message.error('Failed to load user info')
      })
    },
    loadNotificationSettings() {
      this.$api({
        url: 'User/getNotificationSettings',
        method: 'get'
      }).then(res => {
        const settings = res.data || {}
        const enabled = settings.new_invoice || false
        const orderStatusEnabled = settings.order_status_change || false
        this.notifyInvoices = enabled
        this.notifyOrderStatus = orderStatusEnabled
      }).catch(() => {
        // ignore notification settings load failure
      })
    },
    startEdit(key) {
      this.editing = key
      this.tempValue = this.fields[key].value
      this.$nextTick(() => {
        const input = this.$refs.editInput
        if (input && input.length) {
          input[0].focus()
        } else if (input) {
          input.focus()
        }
      })
    },
    confirmEdit(key) {
      if (!this.tempValue.trim()) {
        this.$message.error('Value cannot be empty')
        return
      }
      this.loading = true
      let data = {}
      if (key === 'givenName') {
        data = { given_name: this.tempValue }
      } else if (key === 'familyName') {
        data = { family_name: this.tempValue }
      } else {
        this.fields[key].value = this.tempValue
        this.editing = null
        this.loading = false
        return
      }
      this.$api({
        url: 'User/updateInfo',
        method: 'post',
        data
      }).then(() => {
        this.loadUserInfo()
        this.editing = null
        this.$message.success('Updated successfully')
      }).catch(() => {
        this.$message.error('Failed to update')
      }).finally(() => {
        this.loading = false
      })
    },
    cancelEdit() {
      this.editing = null
    },
    changeEmail() {
      if (!this.newEmail) {
        this.$message.error('Please enter a new email')
        return
      }
      this.loading = true
      this.$api({
        url: 'User/updateInfo',
        method: 'post',
        data: { email: this.newEmail }
      }).then(res => {
        this.$message.success(res.msg || 'Email changed successfully')
        this.loadUserInfo()
        this.newEmail = ''
      }).catch(() => {
        this.$message.error('Failed to change email')
      }).finally(() => {
        this.loading = false
      })
    },
    saveNotifications() {
      this.loading = true
      this.$api({
        url: 'User/saveNotificationSettings',
        method: 'post',
        data: {
          new_invoice: this.notifyInvoices ? 1 : 0,
          order_status_change: this.notifyOrderStatus ? 1 : 0
        }
      }).then(res => {
        this.$message.success(res.msg || 'Notification settings saved')
        this.loadNotificationSettings()
      }).catch(() => {
        this.$message.error('Failed to save notification settings')
      }).finally(() => {
        this.loading = false
      })
    },
    changePassword() {
      if (!this.password_form.current_password || !this.password_form.password || !this.password_form.password_confirmation) {
        this.$message.error('Please fill in all fields')
        return
      }
      if (this.password_form.password != this.password_form.password_confirmation) {
        this.$message.error('New passwords do not match')
        return
      }
      this.loading = true
      this.$api({
        url: 'User/changePassword',
        method: 'post',
        data: {
          ...this.password_form
        }
      }).then(res => {
        if (res.code == 200) {
          this.$message.success(res.msg || 'Password changed successfully')
          this.password_form.current_password = ''
          this.password_form.password = ''
          this.password_form.password_confirmation = ''
          removeToken()
          this.$router.push({ name: 'home' })
        }
      }).catch(() => {
        this.$message.error('Failed to change password')
      }).finally(() => {
        this.loading = false
      })
    },
    deleteAccount() {
      if (!this.allowAddressUse || !this.allowAnalyticsCookies) {
        this.$message.warning('Please agree to both privacy options before deleting your account')
        return
      }
      this.$prompt('Please enter your password to confirm account deletion', 'Delete Account', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        inputType: 'password',
        inputPlaceholder: 'Enter your password',
        confirmButtonClass: 'el-button--danger'
      }).then(({ value }) => {
        if (!value) {
          this.$message.error('Password is required')
          return
        }
        this.$api({
          url: 'User/deleteAccount',
          method: 'post',
        }).then(res => {
          if (res.code == 200) {
            this.$message.success(res.msg || 'Account deleted successfully')
            removeToken()
            this.$router.push({ name: 'home' })
          }
        }).catch(() => {
          this.$message.error('Failed to delete account')
        })
      }).catch(() => {
        // user cancelled
      })
    }
  }
}
</script>

<style scoped>
.settings-page h1 {
  margin: 0 0 32px;
  font-size: 24px;
  font-weight: 400;
}

.settings-page h2 {
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 400;
}

.settings-page p {
  margin: 0 0 18px;
  font-size: 14px;
}

.profile-table {
  width: 100%;
  margin-bottom: 40px;
  border-collapse: collapse;
  font-size: 14px;
}

.profile-table tr {
  border-bottom: 1px solid #eee;
}

.profile-table th {
  width: 260px;
  height: 42px;
  text-align: right;
  font-weight: 400;
  padding-right: 80px;
}

.profile-table td {
  color: #aaa;
}

.profile-table .field-value {
  cursor: pointer;
  display: inline-block;
  min-width: 200px;
  min-height: 28px;
  line-height: 28px;
}

.profile-table .field-value.has-value {
  font-family: inherit;
  font-weight: bold;
  font-size: 16px;
  color: #000000;
  line-height: 19px;
  text-align: left;
}

.profile-table .field-value.empty-value {
  font-family: inherit;
  font-weight: 400;
  font-size: 16px;
  color: #999999;
  line-height: 22px;
}

.profile-table strong {
  color: #333;
}

.profile-table .edit-input {
  width: 360px;
}

.profile-table .edit-input ::v-deep .el-input__inner {
  height: 28px;
  border: 1px solid #ddd;
  padding: 0 12px;
  outline: none;
}

.profile-table .edit-input ::v-deep .el-input__inner:focus {
  border-color: #F34B01;
}

.profile-table button {
  height: 28px;
  margin-left: 8px;
  border: 0;
  border-radius: 3px;
  padding: 0 18px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
}

.profile-table button.cancel-btn {
  background: #F34B01;
}

.plain-section {
  margin-bottom: 34px;
}

.settings-block {
  margin-bottom: 44px;
}

.settings-box {
  padding: 28px 38px;
  border-radius: 6px;
  background: #fff1ed;
  font-size: 13px;
}

.panel-row {
  margin-bottom: 18px;
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: center;
}

.panel-row .panel-input {
  width: 100%;
}

.panel-row .panel-input ::v-deep .el-input__inner {
  width: 100%;
  height: 28px;
  border: 1px solid #ddd;
  outline: none;
}

.panel-row .panel-input ::v-deep .el-input__inner:focus {
  border-color: #F34B01;
}

.primary-btn {
  height: 34px;
  margin-top: 8px;
  border: 0;
  border-radius: 3px;
  padding: 0 22px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
}

.custom-checkbox {
  display: none;
}

.check-box {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  background: url(../../assets/check0.png) no-repeat center/contain;
}

.custom-checkbox:checked+.check-box {
  background-image: url(../../assets/check1.png);
}

.check-line,
.privacy-line {
  margin: 24px 0;
  display: flex;
  gap: 12px;
  align-items: center;
}

.privacy-line {
  align-items: flex-start;
}

.settings-box a {
  color: #F34B01;
}

.privacy-box {
  min-height: 280px;
}

.delete-btn {
  float: right;
  margin-top: 22px;
}
</style>
